import type {
  CreateWhitelabelConfigBody,
  UpdateWhitelabelConfigBody,
  WhitelabelConfig,
} from "@nlc/shared-types";

import { zodResolver } from "@hookform/resolvers/zod";
import { createWhitelabelConfigBodyZod } from "@nlc/shared-types";
import { Controller, useForm } from "react-hook-form";

import { Button } from "@/components/button";
import { FieldError, Input, Label, TextField } from "@/components/field";

interface WhitelabelFormProps {
  initialData?: WhitelabelConfig;
  isEditing?: boolean;
  onCancel: () => void;
  onSubmit: (
    data: CreateWhitelabelConfigBody | UpdateWhitelabelConfigBody,
  ) => void;
}

export function WhitelabelForm({
  initialData,
  isEditing = false,
  onCancel,
  onSubmit,
}: WhitelabelFormProps) {
  const { control, formState, handleSubmit } =
    useForm<CreateWhitelabelConfigBody>({
      defaultValues: {
        customDomain: initialData?.customDomain ?? "",
        faviconUrl: initialData?.faviconUrl ?? "",
        logoUrl: initialData?.logoUrl ?? "",
        ownerId: initialData?.ownerId ?? 0,
        partnerName: initialData?.partnerName ?? "",
        primaryColor: initialData?.primaryColor ?? "",
        privacyUrl: initialData?.privacyUrl ?? "",
        slug: initialData?.slug ?? "",
        supportEmail: initialData?.supportEmail ?? "",
        termsUrl: initialData?.termsUrl ?? "",
      },
      resolver: zodResolver(createWhitelabelConfigBodyZod),
    });

  const onFormSubmit = (data: CreateWhitelabelConfigBody) => {
    if (isEditing) {
      const { ownerId: _, ...updateData } = data;
      const dirtyFields = formState.dirtyFields;
      const partialData: UpdateWhitelabelConfigBody = {};

      for (const key of Object.keys(dirtyFields)) {
        const typedKey = key as keyof typeof updateData;

        if (typedKey in updateData) {
          (partialData as Record<string, unknown>)[typedKey] =
            updateData[typedKey];
        }
      }

      onSubmit(partialData);
    } else {
      onSubmit(data);
    }
  };

  return (
    <form
      className="flex flex-col gap-3xl rounded-b-2xl bg-bg-primary p-2xl"
      onSubmit={handleSubmit(onFormSubmit)}
    >
      <div className="grid grid-cols-1 gap-xl md:grid-cols-2">
        <Controller
          control={control}
          name="partnerName"
          render={({
            field: { name, onBlur, onChange, ref, value },
            fieldState: { error, invalid },
          }) => (
            <TextField
              isInvalid={invalid}
              isRequired
              name={name}
              onBlur={onBlur}
              onChange={onChange}
              validationBehavior="aria"
              value={value}
            >
              <Label>Partner Name</Label>
              <Input placeholder="e.g. Design Partner Inc." ref={ref} />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />

        {!isEditing && (
          <Controller
            control={control}
            name="ownerId"
            render={({
              field: { name, onBlur, onChange, ref, value },
              fieldState: { error, invalid },
            }) => (
              <TextField
                isInvalid={invalid}
                isRequired
                name={name}
                onBlur={onBlur}
                onChange={(v) => onChange(Number(v))}
                validationBehavior="aria"
                value={String(value)}
              >
                <Label>Owner User ID</Label>
                <Input
                  placeholder="User ID of the partner owner"
                  ref={ref}
                  type="number"
                />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
        )}

        <Controller
          control={control}
          name="customDomain"
          render={({
            field: { name, onBlur, onChange, ref, value },
            fieldState: { error, invalid },
          }) => (
            <TextField
              isInvalid={invalid}
              name={name}
              onBlur={onBlur}
              onChange={onChange}
              validationBehavior="aria"
              value={value ?? ""}
            >
              <Label>Custom Domain</Label>
              <Input placeholder="e.g. app.partner.com" ref={ref} />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />

        <Controller
          control={control}
          name="logoUrl"
          render={({
            field: { name, onBlur, onChange, ref, value },
            fieldState: { error, invalid },
          }) => (
            <TextField
              isInvalid={invalid}
              name={name}
              onBlur={onBlur}
              onChange={onChange}
              validationBehavior="aria"
              value={value ?? ""}
            >
              <Label>Logo URL</Label>
              <Input placeholder="https://..." ref={ref} />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />

        <Controller
          control={control}
          name="faviconUrl"
          render={({
            field: { name, onBlur, onChange, ref, value },
            fieldState: { error, invalid },
          }) => (
            <TextField
              isInvalid={invalid}
              name={name}
              onBlur={onBlur}
              onChange={onChange}
              validationBehavior="aria"
              value={value ?? ""}
            >
              <Label>Favicon URL</Label>
              <Input placeholder="https://..." ref={ref} />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />

        <Controller
          control={control}
          name="primaryColor"
          render={({
            field: { name, onBlur, onChange, ref, value },
            fieldState: { error, invalid },
          }) => (
            <TextField
              isInvalid={invalid}
              name={name}
              onBlur={onBlur}
              onChange={onChange}
              validationBehavior="aria"
              value={value ?? ""}
            >
              <Label>Primary Color</Label>
              <Input placeholder="#7C3AED" ref={ref} />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />

        <Controller
          control={control}
          name="supportEmail"
          render={({
            field: { name, onBlur, onChange, ref, value },
            fieldState: { error, invalid },
          }) => (
            <TextField
              isInvalid={invalid}
              name={name}
              onBlur={onBlur}
              onChange={onChange}
              validationBehavior="aria"
              value={value ?? ""}
            >
              <Label>Support Email</Label>
              <Input placeholder="support@partner.com" ref={ref} />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />

        <Controller
          control={control}
          name="termsUrl"
          render={({
            field: { name, onBlur, onChange, ref, value },
            fieldState: { error, invalid },
          }) => (
            <TextField
              isInvalid={invalid}
              name={name}
              onBlur={onBlur}
              onChange={onChange}
              validationBehavior="aria"
              value={value ?? ""}
            >
              <Label>Terms of Service URL</Label>
              <Input placeholder="https://..." ref={ref} />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />

        <Controller
          control={control}
          name="privacyUrl"
          render={({
            field: { name, onBlur, onChange, ref, value },
            fieldState: { error, invalid },
          }) => (
            <TextField
              isInvalid={invalid}
              name={name}
              onBlur={onBlur}
              onChange={onChange}
              validationBehavior="aria"
              value={value ?? ""}
            >
              <Label>Privacy Policy URL</Label>
              <Input placeholder="https://..." ref={ref} />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />
      </div>

      <div className="flex gap-xl justify-end">
        <Button onPress={onCancel} variant="secondary">
          Cancel
        </Button>
        <Button type="submit">
          {isEditing ? "Save Changes" : "Create Config"}
        </Button>
      </div>
    </form>
  );
}
